import { AlertColor } from "@mui/material";

import { setMessage } from "../redux/notification";
import type { AppDispatch } from "../store";

/**
 * Utility function to show a notification message
 * @param content The message content to display
 * @param type The type of notification (error, warning, info, success)
 * @param dispatch The Redux dispatch function
 * @param isLoading Whether the notification is in a loading state
 */
export const showNotification = (
  content: string,
  type: AlertColor = "info",
  dispatch: AppDispatch,
  isLoading = false
) => {
  dispatch(setMessage({ content, type, isLoading }));
};

/**
 * Utility function to show an error notification
 * @param content The error message to display
 * @param dispatch The Redux dispatch function
 */
export const showError = (content: string, dispatch: AppDispatch) => {
  showNotification(content, "error", dispatch);
};

/**
 * Utility function to show a success notification
 * @param content The success message to display
 * @param dispatch The Redux dispatch function
 */
export const showSuccess = (content: string, dispatch: AppDispatch) => {
  showNotification(content, "success", dispatch);
};

/**
 * Utility function to show a warning notification
 * @param content The warning message to display
 * @param dispatch The Redux dispatch function
 */
export const showWarning = (content: string, dispatch: AppDispatch) => {
  showNotification(content, "warning", dispatch);
};

/**
 * Utility function to show an info notification
 * @param content The info message to display
 * @param dispatch The Redux dispatch function
 */
export const showInfo = (content: string, dispatch: AppDispatch) => {
  showNotification(content, "info", dispatch);
};

/**
 * Utility function to show a loading notification
 * @param content The loading message to display
 * @param dispatch The Redux dispatch function
 */
export const showLoading = (content: string, dispatch: AppDispatch) => {
  showNotification(content, "info", dispatch, true);
};

/**
 * Utility function to clear any active notification
 * @param dispatch The Redux dispatch function
 */
export const clearNotification = (dispatch: AppDispatch) => {
  dispatch(setMessage(null));
};

/**
 * Convert a technical error into a short, user-friendly message.
 *
 *
 * Current mappings (examples):
 * - blocked by the browser's security policy -> "The upload was blocked by this site's security policy..."
 * - network -> "Network error. Please check your connection and try again."
 * - failed to fetch -> "Unable to load the requested information. Please try again."
 * - size / file too large / payload -> "The selected file is too large. Please try a smaller file."
 * - permission / unauthorized / 403 -> "You don't have permission to perform this action. Please try signing in again or contact support."
 * - other errors -> "Something went wrong. Please try again."
 *
 * @param err - An Error or other thrown value
 * @returns short user-friendly message
 */
export const getFriendlyErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    // Must precede the "network" branch — a request refused by the page's
    // Content-Security-Policy is NOT a connectivity problem, and telling the
    // user to check their connection points them away from the actual cause.
    // Raised by the direct-to-blob uploaders, and only on a CONFIRMED policy
    // violation (the browser's securitypolicyviolation event) — a failure that
    // merely looks similar stays on the connectivity wording below.
    if (msg.includes("blocked by the browser's security policy")) {
      return "The upload was blocked by this site's security policy, not by your connection. Please contact support — it needs a configuration change.";
    }
    if (msg.includes("network")) {
      return "Network error. Please check your connection and try again.";
    }
    if (msg.includes("failed to fetch")) {
      return "Unable to load the requested information. Please try again.";
    }
    if (
      msg.includes("size") ||
      msg.includes("file too large") ||
      msg.includes("payload")
    ) {
      return "The selected file is too large. Please try a smaller file.";
    }
    // MDRS-624: the document-request route refuses a consent blob path outside
    // the case's own Consent prefix with a 403. Distinct from the classify
    // branch below -- there is no bundle in the referrer's flow, so that copy
    // would tell them to re-upload something that doesn't exist. Matches on
    // "consent isn't part of this case"; the backend detail is pinned verbatim
    // in notification.test.ts.
    if (msg.includes("consent isn't part of this case")) {
      return "That signed consent isn't part of this case. Please re-attach it and try again.";
    }
    // MDRS-624: RequestDocumentsForm stopped passing a customMessage (which was
    // swallowing every mapping on that path), so the only-once 409 reaches here
    // now. Without this it lands on the generic fallback, which reads as a
    // transient failure and invites a retry that can only 409 again.
    if (msg.includes("document request already exists")) {
      return "A document request has already been raised for this case.";
    }
    // MDRS-624: the provider-dispatch 502. Without a branch it lands on the
    // generic fallback, which is vaguer than the flat customMessage the form
    // used to pass -- so removing that argument would have made this case
    // worse, not better. Names the provider so it reads as "their end, retry"
    // rather than "your request was wrong".
    if (msg.includes("submit the request to the provider")) {
      return "Couldn't send the request to the provider. Please try again shortly.";
    }
    // MDRS-546: classify refuses blob paths outside the case's own prefix with
    // a 403 whose body contains "permission", which would otherwise fall into
    // the generic branch below and tell the user to sign in again -- advice
    // that can never fix this. Must stay ABOVE that branch.
    if (msg.includes("not part of this case")) {
      return "Those files aren't part of this case. Please re-upload the bundle and try again.";
    }
    if (
      msg.includes("permission") ||
      msg.includes("unauthorized") ||
      msg.includes("403") ||
      // MDRS-443: the generic body every backend require_capability gate
      // returns when a user-type's access-config toggle is off. Doesn't
      // contain "permission"/"403" itself, and JSON.stringify-ing the body
      // (see buildApiErrorMessage) never captures the HTTP status either.
      msg.includes("don't have access to this feature")
    ) {
      return "You don't have permission to perform this action. Please try signing in again or contact support.";
    }
    // Finalise refused because the case's documents are still being uploaded or
    // summarised (MDRS-508). Also a 409, but the case is NOT locked — so this
    // has to be checked before the locked branch below.
    if (msg.includes("still being uploaded or processed")) {
      return "Documents are still being uploaded or processed for this case. You can finalise the report once they've finished.";
    }
    // Finalised/locked case (MDRS-271): a mutating request returns 409 once the
    // report has been finalised. Surface the doctor-facing meaning, not the raw
    // error body.
    if (msg.includes("409") || msg.includes("finalised") || msg.includes("locked")) {
      return "This report has been finalised and the case is locked. It can no longer be edited.";
    }
    // Generic fallback
    return "Something went wrong. Please try again.";
  }
  return "An unexpected error occurred. Please try again.";
};

/**
 * Build a "Failed to <do the thing>: <reason>" message for a mutation/query
 * hook's thrown Error, preferring the FastAPI error body's `detail` string
 * (e.g. "You don't have access to this feature.") over dumping the raw
 * response body — a JSON.stringify'd error object reads as an opaque blob in
 * a toast, not a message (MDRS-443, generalising MDRS-390's
 * buildExportErrorMessage so every hook shares one implementation).
 *
 * This only shapes the *body* of the thrown Error; feeding the result through
 * `getFriendlyErrorMessage`/`notifyError` at the point the toast is shown is
 * still what maps it to fully user-facing copy — this just guarantees that
 * even a caller that reads `error.message` directly (bypassing that mapping)
 * sees the backend's own human-readable reason instead of raw JSON.
 */
export const buildApiErrorMessage = (prefix: string, error: unknown): string => {
  const detail =
    error && typeof error === "object" && "detail" in error
      ? (error as { detail?: unknown }).detail
      : undefined;

  return `${prefix}: ${typeof detail === "string" ? detail : JSON.stringify(error)}`;
};

/**
 * Log a technical error and show a short, user-friendly toast.
 *
 * Usage patterns:
 * - notifyError(err, dispatch)
 *     Logs `err` and shows a friendly mapped message.
 * - notifyError(err, dispatch, "Custom message")
 *     Logs `err` and shows the provided custom message instead of the mapped one.
 * - notifyError(undefined, dispatch, "Custom message only")
 *     Shows the provided message without logging an error.
 *
 *
 * @param err - The thrown error (optional if customMessage is provided)
 * @param dispatch - Redux dispatch function
 * @param customMessage - Override the computed friendly message
 */
export const notifyError = (
  err: unknown,
  dispatch: AppDispatch,
  customMessage?: string
) => {
  // Preserve full technical error in console for debugging
  if (err !== undefined) console.error(err);

  const message = customMessage || getFriendlyErrorMessage(err);
  showError(message, dispatch);
};
